<template>
  <b-card no-body>
    <b-card-header>
      <b-card-title class="mb-50">
        {{ $t('survey.attendee_answers') }}
      </b-card-title>
    </b-card-header>

    <b-card-body>
      <div class="d-flex justify-content-end mb-3">
        <download-excel type="csv" class="btn btn-outline-info" :name="item + '.csv'" :data="dataForExcel">
          {{ $t('survey.download_excel') }}
        </download-excel>
      </div>
      <!-- Chart Section -->
      <vue-apex-charts
        v-if="columnChart && columnChart.series.length > 0"
        type="bar"
        height="400"
        :options="columnChart.chartOptions"
        :series="columnChart.series"
        :key="chartKey"
      />
      
      <div v-else-if="data && data.length === 0" class="text-center p-3">
        <p>{{ $t('No matching records found') }}</p>
      </div>
      
      <!-- Table Section -->
      <b-table responsive="sm" :items="items" :fields="tableColumns" show-empty
        :empty-text="$t('No matching records found')">
        <template #cell(TAG)="data">
          <b-media vertical-align="center">
            <template #aside>
            </template>
            {{ data.item.tag }}
          </b-media>
        </template>
        <template v-for="index in tableColumns.length" v-slot:[`cell(${index})`]="data">
          {{ calculateData(data.item, index) }}
        </template>
      </b-table>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BTable, BCard, BCardBody, BCardHeader, BCardTitle, BMedia,
} from 'bootstrap-vue'
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import VueApexCharts from 'vue-apexcharts'
import flatPickr from 'vue-flatpickr-component'
import apexChatData from './apexChartData'
import downloadExcel from 'vue-json-excel'

export default {
  components: {
    BCardCode,
    BMedia,
    BTable,
    BCard,
    BCardBody,
    BCardHeader,
    VueApexCharts,
    flatPickr,
    BCardTitle,
    downloadExcel
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    item: {
      type: String,
      default: () => '',
    },
  },
  computed: {
    // Chart key for force re-render when data changes
    chartKey() {
      return JSON.stringify(this.data?.slice(0, 3) || []) + Date.now();
    },

    chartColors() {
      return {
        column: {
          series1: '#826af9',
          series2: '#d2b0ff',
          bg: '#f8d3ff',
        },
        success: {
          shade_100: '#7eefc7',
          shade_200: '#06774f',
        },
        donut: {
          series1: '#ffe700',
          series2: '#00d4bd',
          series3: '#826bf8',
          series4: '#2b9bf4',
          series5: '#FFA1A1',
        },
        area: {
          series3: '#a4f8cd',
          series2: '#60f2ca',
          series1: '#2bdac7',
        },
      }
    },
    
    processedData() {
      if (!this.data || this.data.length === 0) {
        return {
          nameGroup: {},
          tagGroup: {},
          seriGroup: [],
          names: [],
          tableColumns: [{ key: "TAG" }],
          tableItems: []
        };
      }

      const groupBy = function (xs, key) {
        return xs.reduce(function (rv, x) {
          (rv[x[key]] = rv[x[key]] || []).push(x);
          return rv;
        }, {});
      };

      const getFields = function(input, field) {
        var output = [];
        for (var i = 0; i < input.length; ++i)
          output.push(input[i][field]);
        return output;
      }

      const nameGroup = groupBy(this.data, 'attendetitle');
      const tagGroup = groupBy(this.data, 'questionTag');
      const seriGroup = [];

      for (var key in nameGroup) {
        var results = getFields(nameGroup[key], "attendeeAnswer");
        seriGroup.push({ name: key, data: results });
      }

      const names = getFields(seriGroup, "name");

      const tableColumns = [{ key: "TAG" }];
      const tableItems = [];

      for (var key in tagGroup) {
        var results = tagGroup[key];
        tableItems.push({ tag: key, data: results, names: names, nameGroup: nameGroup });
      }

      for (var i = 0; i < names.length; ++i)
        tableColumns.push({ key: (i + 1).toString(), label: names[i] });

      return {
        nameGroup,
        tagGroup,
        seriGroup,
        names,
        tableColumns,
        tableItems
      };
    },

    names() {
      return this.processedData.names;
    },

    items() {
      return this.processedData.tableItems;
    },

    tableColumns() {
      return this.processedData.tableColumns;
    },

    columnChart() {
      const { seriGroup, names,tagGroup } = this.processedData;
      const columns = Object.keys(tagGroup);
      return {
        series: seriGroup,
        chartOptions: {
          chart: {
            stacked: false,
            toolbar: {
              show: false,
            },
          },
          
          dataLabels: {
            enabled: true,
          },
          legend: {
            show: true,
            position: 'top',
            fontSize: '14px',
            fontFamily: 'Montserrat',
            horizontalAlign: 'left',
          },
          stroke: {
            show: true,
            colors: ['transparent'],
          },
          grid: {
            xaxis: {
              lines: {
                show: true,
              },
            },
          },
          xaxis: {
            categories: columns,
          },
          yaxis: {
            // opposite: isRtl,
          },
          fill: {
            opacity: 1,
          },
          responsive: [{
            breakpoint: 480,
            options: {
              chart: {
                width: '100%'
              },
              legend: {
                position: 'bottom'
              }
            }
          }]
        },
      };
    },

    dataForExcel() {
      const getFields = function(input, field) {
        var output = [];
        for (var i = 0; i < input.length; ++i)
          output.push(input[i][field]);
        return output;
      }

      const groupBy = function (xs, key) {
        return xs.reduce(function (rv, x) {
          (rv[x[key]] = rv[x[key]] || []).push(x);
          return rv;
        }, {});
      };

      // Data kontrolü ekle
      if (!this.data || this.data.length === 0) {
        return [];
      }

      const nameGroup = groupBy(this.data, 'attendetitle');
      const tagGroup = groupBy(this.data, 'questionTag');
      const seriGroup = [];

      for (var key in nameGroup) {
        var results = getFields(nameGroup[key], "attendeeAnswer");
        seriGroup.push({ name: key, data: results });
      }

      const names = getFields(seriGroup, "name");
      const tableColumns = [{ key: "TAG" }];
      const tableItems = [];
      const tagkeys = [];

      for (var key in tagGroup) {
        tagkeys.push(key);
        var results = tagGroup[key];
        tableItems.push({ tag: key, data: results, names: names, nameGroup: nameGroup });
      }

      for (var i = 0; i < names.length; ++i)
        tableColumns.push({ key: (i + 1).toString(), label: names[i] });
      
      const excItems = [];
      for (var k = 0; k < tagkeys.length; ++k) {
        var currentTag = tagkeys[k];
        var element = {};

        element["TAG"] = currentTag;

        for (var ll = 0; ll < names.length; ++ll) {
          var objd = tableItems.find(item => item.tag === currentTag);
          if (objd && objd.nameGroup && objd.nameGroup[names[ll]]) {
            var obj = objd.nameGroup[names[ll]];

            if (Array.isArray(obj)) {
              var data = obj.find(item => item.questionTag === currentTag);
              if (data) {
                element[names[ll]] = data.attendeeAnswer;
              }
            }
          }
        }

        excItems.push(element);
      }

      return excItems;
    }
  },
  
  methods: {
    calculateData(tag, column) {
      var incomingTag = tag.tag;
      var names = tag.names;
      var info = names[column - 1];

      if (!tag.nameGroup || !tag.nameGroup[info]) {
        return '';
      }

      var rateInfo = tag.nameGroup[info];
      var result = rateInfo.filter(obj => {
        return obj.questionTag === incomingTag
      })
      
      if (result.length === 0) {
        return '';
      }
      
      var answer = result[0];
      return answer.attendeeAnswer;
    }
  },

  data() {
    return {
      apexChatData,
    }
  },

  // Watch prop changes to ensure reactivity
  watch: {
    data: {
      handler(newData, oldData) {
        // console.log('Data prop changed:', newData);
      },
      deep: true,
      immediate: true
    }
  }
}
</script>
